import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface Question {
  id: string;
  type: string;
  score: number;
  [key: string]: any;
}

interface InsertQuestionModalProps {
  isOpen: boolean;
  currentQuestion: Question;
  currentIndex: number;
  totalQuestions: number;
  onConfirm: (position: 'before' | 'after') => void;
  onCancel: () => void;
}

const typeNames: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  python_coding: 'Python编程题',
  cpp_coding: 'C++编程题',
  scratch_coding: 'Scratch编程题',
};

export default function InsertQuestionModal({
  isOpen,
  currentQuestion,
  currentIndex,
  totalQuestions,
  onConfirm,
  onCancel,
}: InsertQuestionModalProps) {
  const [position, setPosition] = useState<'before' | 'after'>('after');

  React.useEffect(() => {
    if (isOpen) {
      setPosition('after');
    }
  }, [isOpen]);

  const typeName = typeNames[currentQuestion.type] || currentQuestion.type;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            className="fixed inset-0 bg-black/50 z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 pointer-events-auto">
              {/* 头部 */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">插入新题目</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    当前第 {currentIndex + 1} 题（共 {totalQuestions} 题）
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* 内容区 */}
              <div className="px-6 py-5 space-y-5">
                {/* 当前题目信息 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">将插入与当前题目相同题型和分值的新题：</p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      第 {currentIndex + 1} 题
                    </span>
                    <span className="text-sm text-gray-700">
                      {typeName} · {currentQuestion.score}分
                    </span>
                  </div>
                </div>

                {/* 位置选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    选择插入位置
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPosition('before')}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        position === 'before'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className={`text-sm font-medium ${
                          position === 'before' ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          本题之前插入
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          成为第 {currentIndex + 1} 题
                        </div>
                      </div>
                      {position === 'before' && (
                        <div className="absolute top-2 right-2">
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>

                    <button
                      onClick={() => setPosition('after')}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        position === 'after'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className={`text-sm font-medium ${
                          position === 'after' ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          本题之后插入
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          成为第 {currentIndex + 2} 题
                        </div>
                      </div>
                      {position === 'after' && (
                        <div className="absolute top-2 right-2">
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* 提示信息 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <span className="font-medium">提示：</span>
                    新题目将继承本题的题型和分值（{typeName} · {currentQuestion.score}分），创建后可直接编辑题目内容。
                  </p>
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => onConfirm(position)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  确认插入
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}